import {noop} from '@benchling/js-utils';
import produce from 'immer';
import {useCallback} from 'react';

import {assert} from 'Asserts';
import useOnMount from 'ui-library/hooks/useOnMount';
import * as logging from 'util/Logging';

import {ProcedureFlowchart, ProcedureMetadata, ProcedureWithMetadata} from '../ProcedureDomainTypes';
import {PersistenceInfo, PreSaveImmediatelyMiddleware} from '../ProcedurePageContext';
import AutosaveManager from '../redux/autosaveManager';
import {saveHandlerFactory} from '../redux/proceduresSaveHandler';

export interface UseLegacyProcedureSaveToolsParams {
  isProcedurePoW: boolean;
  procedureId: string;
  processFlowchartConfigId: string | null;
  draftFlowchartRef: React.MutableRefObject<ProcedureFlowchart>;
  legacyMiddlewareRef: React.MutableRefObject<Array<{methodCid: string; fn: PreSaveImmediatelyMiddleware}>>;
  setLocalDraftFlowchartRef: React.MutableRefObject<
    (updater: (fc: ProcedureFlowchart) => ProcedureFlowchart) => void
  >;
  setProcedureMetadata: (metadata: ProcedureMetadata) => void;
  updateAndPersistLegacyProcedure: (
    procedure: ProcedureWithMetadata,
    context?: PersistenceInfo
  ) => Promise<null | {updatedProcedure: ProcedureWithMetadata; cidToIdMap: Record<string, string>}>;
  setPendingDraftFlowchartUpdate: (
    update: {updater: (fc: ProcedureFlowchart) => ProcedureFlowchart} | null
  ) => void;
  lastSavedFlowchartVersionId: React.MutableRefObject<string>;
  saveProcedureWhenUnmountedFactory: (
    lastSavedFlowchartVersionId: string
  ) => (procedure: ProcedureWithMetadata, persistenceInfo?: PersistenceInfo) => Promise<null>;
}

type LegacySaveImmediatelyFunction = (
  applyChangesAfterInflightSaves: (latestFlowchart: ProcedureFlowchart) => PersistenceInfo,
  overlayMessage?: string,
  shouldBubbleUpErrors?: boolean
) => Promise<ProcedureMetadata>;

/**
 * Hook that manages the legacy procedure save functionality, including:
 * - The saveImmediately function for immediate saves with middleware support
 * - Autosave handler setup and cleanup
 *
 * Note: This hook only handles legacy procedures. PoW procedures use a different autosave mechanism.
 */
export default function useLegacyProcedureSaveTools({
  isProcedurePoW,
  procedureId,
  processFlowchartConfigId,
  draftFlowchartRef,
  legacyMiddlewareRef,
  setLocalDraftFlowchartRef,
  setProcedureMetadata,
  updateAndPersistLegacyProcedure,
  setPendingDraftFlowchartUpdate,
  lastSavedFlowchartVersionId,
  saveProcedureWhenUnmountedFactory,
}: UseLegacyProcedureSaveToolsParams): LegacySaveImmediatelyFunction {
  /**
   * Use this function when the user takes an action which requires an immediate server call.
   * IMPORTANT: you must make changes to the procedure inside the callback, not before
   * invoking this function! And be sure to apply your updates to the flowchart provided
   * in the callback.
   *
   * GOOD:
   *    saveImmediately((latestFlowchart) => {applyUpdates(latestFlowchart, updates)});
   *
   * BAD:
   *    applyUpdates(props.flowchart, updates);
   *    saveImmediately(noop);
   */
  const legacySaveImmediately = useCallback(
    (
      applyChangesAfterInflightSaves: (latestFlowchart: ProcedureFlowchart) => PersistenceInfo,
      overlayMessage?: string,
      shouldBubbleUpErrors: boolean = true
    ): Promise<ProcedureMetadata> => {
      // The PoW autosaver is managed elsewhere
      assert(!isProcedurePoW, 'legacySaveImmediately should not be called for PoW procedures');

      const prepAfterInflightSaves = (): PersistenceInfo => {
        let middlewareFlowchart = draftFlowchartRef.current;
        for (const {methodCid, fn: preSaveFunction} of legacyMiddlewareRef.current) {
          const originalMethod = middlewareFlowchart.nodes.find((m) => m.cid === methodCid);
          if (originalMethod != null) {
            const updatedMethod = preSaveFunction(originalMethod);
            middlewareFlowchart = produce(middlewareFlowchart, (fc) => {
              const index = fc.nodes.findIndex((m) => m.cid === methodCid);
              fc.nodes[index] = updatedMethod;
            });
          } else {
            logging.warn('Middleware did not find method', {extra: {methodCid}});
          }
        }
        setLocalDraftFlowchartRef.current(() => middlewareFlowchart);
        return applyChangesAfterInflightSaves(middlewareFlowchart);
      };

      return AutosaveManager.saveImmediately(
        procedureId,
        prepAfterInflightSaves,
        overlayMessage,
        shouldBubbleUpErrors
      );
    },
    // Refs are included in deps to satisfy exhaustive-deps lint rule. The refs themselves are stable
    // (they don't change), but we include them because the callback accesses their .current values.
    [procedureId, draftFlowchartRef, legacyMiddlewareRef, setLocalDraftFlowchartRef, isProcedurePoW]
  );

  // Setup autosave handler on mount
  useOnMount(() => {
    // The PoW autosaver is managed elsewhere
    if (isProcedurePoW) {
      return;
    }

    AutosaveManager.updateSaveHandler(
      procedureId,
      saveHandlerFactory(
        setProcedureMetadata,
        updateAndPersistLegacyProcedure,
        setPendingDraftFlowchartUpdate,
        processFlowchartConfigId
      )
    );

    // On unmount, we immediately swap out to a "clean" save handler, which performs no internal bookkeeping
    return () => {
      AutosaveManager.updateSaveHandler(
        procedureId,
        saveHandlerFactory(
          noop,
          saveProcedureWhenUnmountedFactory(lastSavedFlowchartVersionId.current),
          noop,
          processFlowchartConfigId
        )
      );
    };
  });

  return legacySaveImmediately;
}
